/**
 * As sessões de trabalho, portadas de knowledge.js:255-435.
 *
 * Duas memórias na mesma tela, e a diferença entre elas é o ponto: o diário
 * bruto (tentativa, beco, comando) vale por sessão e nunca entra na busca
 * semântica; o resumo curado vira unidade do acervo e é recuperável para sempre.
 * O diário fica recolhido porque ninguém lê 200 eventos — mas quem precisa, abre.
 *
 * O resumo nasce "não confirmado" de propósito: já é buscável, e o dono do
 * módulo confirma ou contesta. Contestar exige motivo, porque o motivo abre a
 * lacuna que alguém vai resolver.
 *
 * O que muda em relação ao painel atual: a sessão aberta está na URL (`?id=`),
 * então o grafo da memória e a paleta chegam aqui por link em vez de variável
 * global (`SESS_OPEN_ID`).
 */
import { useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { postar } from '../api/cliente';
import { chaves, useDocs, useSessaoAberta, useSessoes } from '../api/consultas';
import type { Sessao } from '../api/tipos';
import { useSessao } from '../auth/sessao';
import { acionavel } from '../componentes/acionavel';
import { Carregando, Falha, Vazio } from '../componentes/Estado';
import { copiar, useRecado } from '../componentes/Recado';
import { CARIMBO_DO_RESUMO, ESPECIE_DE_EVENTO, SITUACAO_DA_SESSAO, rotulo, shaCurto } from '../dados/rotulos';
import { md } from '../md/markdown';

type Filtro = 'all' | 'open' | 'unconfirmed' | 'mine' | 'closed';

const FILTROS: [Filtro, string][] = [
  ['all', 'todas'],
  ['open', 'abertas'],
  ['unconfirmed', 'a confirmar'],
  ['mine', 'minhas'],
  ['closed', 'fechadas'],
];

/** Duração legível entre dois carimbos do banco (`YYYY-MM-DD HH:MM:SS`, UTC). */
export function intervaloHumano(a?: string | null, b?: string | null): string | null {
  if (!a || !b) return null;
  const ms = +new Date(`${b.replace(' ', 'T')}Z`) - +new Date(`${a.replace(' ', 'T')}Z`);
  if (!isFinite(ms) || ms < 0) return null;
  const m = Math.round(ms / 60000);
  return m < 60 ? `${m} min` : `${Math.floor(m / 60)}h${String(m % 60).padStart(2, '0')}`;
}

export function Sessoes({ slug }: { slug: string }) {
  const avisar = useRecado();
  const { eu } = useSessao();
  const consulta = useSessoes(slug);
  const [parametros, setParametros] = useSearchParams();
  const [filtro, setFiltro] = useState<Filtro>('all');

  const aberta = parametros.get('id');
  const alternar = (id: string) => {
    const p = new URLSearchParams(parametros);
    if (aberta === id) p.delete('id'); else p.set('id', id);
    setParametros(p, { replace: true });
  };

  if (consulta.isPending) return <Carregando rotulo="as sessões de trabalho" />;
  if (consulta.error) {
    return <Falha erro={consulta.error} rotulo="as sessões de trabalho" aoRepetir={consulta.refetch} />;
  }

  const sessoes = consulta.data?.sessions || [];
  const naoConfirmadas = consulta.data?.unconfirmed || [];
  const idsNaoConfirmados = new Set(naoConfirmadas.map((u) => u.id));
  const abertas = sessoes.filter((s) => s.status === 'open');
  const minhas = (s: Sessao) => !!eu?.email
    && String(s.author_email || '').toLowerCase() === eu.email.toLowerCase();

  const lista = sessoes.filter((s) => (
    filtro === 'all' ? true
      : filtro === 'mine' ? minhas(s)
        : filtro === 'unconfirmed' ? idsNaoConfirmados.has(s.id)
          : s.status === filtro
  ));

  return (
    <>
      <div className="crumb"><b>{slug}</b><span className="sep">/</span>sessões</div>
      <div className="kb-head">
        <div>
          <div className="sec-title">Sessões de trabalho · {sessoes.length}</div>
          <div className="sec-sub">
            O agente abre a sessão ao começar a tarefa (<code>session_start</code>), registra o que
            tentou e o que descobriu, e fecha com um resumo (<code>session_close</code>). O{' '}
            <b>diário</b> fica na sessão e nunca vai para a busca; o <b>resumo</b> entra no acervo
            como unidade curada e é recuperável para sempre. Assim ninguém recomeça do zero na
            próxima tarefa do mesmo assunto.
          </div>
        </div>
        <div className="kb-actions">
          <div className="seg" id="sess-filter">
            {FILTROS.map(([v, l]) => (
              <button key={v} type="button" className={filtro === v ? 'on' : ''} onClick={() => setFiltro(v)}>
                {l}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="btn ghost sm"
            onClick={async () => {
              avisar(await copiar('eng session start "descreva a tarefa"')
                ? 'comando copiado'
                : 'não consegui copiar — selecione o texto e use Ctrl+C');
            }}
          >
            copiar <code>eng session start</code>
          </button>
        </div>
      </div>

      {!!naoConfirmadas.length && (
        <div className="warn-line">
          {naoConfirmadas.length} resumo(s) de sessão esperando confirmação do dono. Resumo não
          confirmado vale menos que decisão e nota — e a busca avisa isso ao agente.
        </div>
      )}

      <div className="hcards">
        <div className={`hcard${abertas.length ? ' alert' : ''}`}>
          <div className="n">{abertas.length}</div><div className="l">sessões abertas</div>
        </div>
        <div className="hcard">
          <div className="n">{sessoes.filter((s) => s.summary_unit_key).length}</div>
          <div className="l">resumos no acervo</div>
        </div>
        <div className={`hcard${naoConfirmadas.length ? ' alert' : ''}`}>
          <div className="n">{naoConfirmadas.length}</div><div className="l">a confirmar</div>
        </div>
        <div className="hcard">
          <div className="n">{sessoes.reduce((a, s) => a + (s.events || 0), 0)}</div>
          <div className="l">eventos no diário</div>
        </div>
      </div>

      {lista.length
        ? (
          <div className="tl">
            {lista.map((s) => (
              <CartaoDeSessao
                key={s.id}
                slug={slug}
                s={s}
                naoConfirmada={idsNaoConfirmados.has(s.id)}
                aberta={aberta === s.id}
                aoAlternar={() => alternar(s.id)}
              />
            ))}
          </div>
        )
        : filtro === 'all'
          ? (
            <Vazio
              titulo="Nenhuma sessão registrada ainda"
              comando={'eng session start "corrigir retry do dispatch"'}
            >
              Sessão é o que impede a próxima pessoa de repetir o caminho que você já andou: o que
              tentou, o que não funcionou e por quê. Abra uma no terminal antes de começar a tarefa —
              no Cursor o agente abre sozinho quando a regra do <code>AGENTS.md</code> está no lugar.
            </Vazio>
          )
          : (
            <Vazio
              titulo={`Nenhuma sessão ${
                filtro === 'open' ? 'aberta agora'
                  : filtro === 'mine' ? 'sua'
                    : filtro === 'unconfirmed' ? 'esperando confirmação' : 'fechada'
              }`}
            >
              Troque o filtro acima para ver as outras sessões do projeto.
            </Vazio>
          )}
    </>
  );
}

function CartaoDeSessao({ slug, s, naoConfirmada, aberta, aoAlternar }: {
  slug: string;
  s: Sessao;
  naoConfirmada: boolean;
  aberta: boolean;
  aoAlternar: () => void;
}) {
  const duracao = intervaloHumano(s.started_at, s.closed_at);
  return (
    <div className={`tl-item${s.status === 'open' ? ' runbook' : ''}`} data-sess={s.id}>
      <div
        className={`tl-card prop${naoConfirmada ? ' pending' : ''}`}
        style={{ padding: 0, border: 'none', background: 'none' }}
      >
        <div aria-expanded={aberta} style={{ cursor: 'pointer' }} {...acionavel(aoAlternar)}>
          <h4>
            <span className={`tag ${s.status === 'open' ? 'runbook' : 'decisao'}`}>
              {rotulo(SITUACAO_DA_SESSAO, s.status)}
            </span>
            {' '}{s.task}{' '}
            {naoConfirmada && <span className="tag drift">a confirmar</span>}
          </h4>
          <div className="when">
            {s.author_email || '—'} · {(s.started_at || '').slice(0, 16).replace('T', ' ')}
            {duracao ? ` · ${duracao}` : ''}
            {s.branch ? <> · <code>{s.branch}</code></> : ''}
            {s.base_sha ? ` · @${shaCurto(s.base_sha)}` : ''}
            {' '}· {s.events || 0} evento(s)
            {s.summary_version ? ` · resumo v${s.summary_version}` : ''}
          </div>
        </div>
        <div className={`sess-body${aberta ? ' show' : ''}`}>
          {aberta && <CorpoDaSessao slug={slug} id={s.id} />}
        </div>
      </div>
    </div>
  );
}

function CorpoDaSessao({ slug, id }: { slug: string; id: string }) {
  const avisar = useRecado();
  const navegar = useNavigate();
  const fila = useQueryClient();
  const { eu } = useSessao();
  const docs = useDocs(slug);
  const consulta = useSessaoAberta(slug, id);
  const [nota, setNota] = useState('');
  const [fechando, setFechando] = useState(false);

  const recarregar = () => Promise.all([
    fila.invalidateQueries({ queryKey: chaves.sessoes(slug) }),
    fila.invalidateQueries({ queryKey: chaves.sessao(slug, id) }),
    fila.invalidateQueries({ queryKey: chaves.lacunas(slug) }),
  ]);

  const decidir = useMutation({
    mutationFn: (acao: 'confirm' | 'contest') => postar(
      `/api/${slug}/sessions/${encodeURIComponent(id)}/${acao}`,
      { note: nota.trim() || null },
    ),
    onSuccess: async (_r, acao) => {
      avisar(acao === 'confirm'
        ? 'resumo confirmado — sobe de camada na busca'
        : 'resumo contestado e lacuna aberta');
      await recarregar();
    },
    onError: (e) => avisar(e instanceof Error ? e.message : String(e)),
  });

  if (consulta.isPending) return <Carregando rotulo="abrindo o diário da sessão…" />;
  if (consulta.error) {
    const msg = consulta.error instanceof Error ? consulta.error.message : String(consulta.error);
    return <div className="warn-line">Não deu para abrir esta sessão: {msg}</div>;
  }

  const d = consulta.data!;
  const minha = !!eu?.email && String(d.author_email || '').toLowerCase() === eu.email.toLowerCase();
  const carimbo = d.summary?.review_state || null;
  const podeDecidir = !!d.can_review && carimbo === 'nao_confirmada';
  const eventos = d.events || [];

  // chave documentada abre a doc; o resto só existe no grafo do AST, e mandar
  // para lá é mais honesto que abrir uma doc vazia
  const ir = (chave: string) => {
    if ((docs.data || []).some((x) => x.unit_key === chave) || chave.startsWith('__')) {
      navegar(`/p/${slug}/doc?key=${encodeURIComponent(chave)}`);
      return;
    }
    navegar(`/p/${slug}/graph?no=${encodeURIComponent(chave)}`);
  };

  const Alvo = ({ chave, children }: { chave: string; children: ReactNode }) => (
    <code className="lnk" title={chave} {...acionavel(() => ir(chave), true)}>
      {children}
    </code>
  );

  return (
    <>
      {d.summary
        ? (
          <div className="sess-sum">
            <div className="col-t">
              resumo no acervo · v{d.summary.version} ·{' '}
              <span className={`tag ${
                carimbo === 'confirmada' ? 'approved' : carimbo === 'contestada' ? 'rejected' : 'pending'
              }`}
              >
                {carimbo ? rotulo(CARIMBO_DO_RESUMO, carimbo) : '—'}
              </span>
            </div>
            <div className="col-b" dangerouslySetInnerHTML={{ __html: md(d.summary.body_md) }} />
            {!!d.summary.links?.length && (
              <div className="tl-links">
                {d.summary.links.map((l) => (
                  <span key={l} className="lnk" title={l} {...acionavel(() => ir(l), true)}>
                    {l}
                  </span>
                ))}
              </div>
            )}
          </div>
        )
        : (
          <div className="muted" style={{ fontSize: 12.5, padding: '8px 0' }}>
            Sessão {d.status === 'open' ? 'aberta — nada no acervo até fechar com resumo' : 'sem resumo publicado'}.
          </div>
        )}

      {d.events_hidden
        ? (
          <div className="muted" style={{ fontSize: 12.5 }}>
            O diário bruto é de quem trabalhou: visível para o autor, o dono do módulo e admin.
          </div>
        )
        : (
          <details className="sess-diary" open={d.status === 'open'}>
            <summary>
              diário de trabalho · {eventos.length} evento(s) — fica fora da busca semântica de propósito
            </summary>
            <div className="ev-list">
              {eventos.length
                ? eventos.map((e, i) => (
                  <div key={i} className={`ev ${e.kind}`}>
                    <span className="ev-k">{rotulo(ESPECIE_DE_EVENTO, e.kind)}</span>
                    <span className="ev-b">{String(e.body || '').slice(0, 600)}</span>
                    {e.unit_key && <Alvo chave={e.unit_key}>{e.unit_key}</Alvo>}
                    <span className="ev-t">{(e.created_at || '').slice(11, 16)}</span>
                  </div>
                ))
                : <div className="muted" style={{ fontSize: 12.5 }}>nenhum evento registrado</div>}
            </div>
          </details>
        )}

      <div className="prop-acts">
        {podeDecidir
          ? (
            <>
              <input
                value={nota}
                onChange={(e) => setNota(e.target.value)}
                placeholder="comentário (se contestar, é o motivo que abre a lacuna)"
              />
              <button
                type="button"
                className="btn ok"
                disabled={decidir.isPending}
                onClick={(e) => { e.stopPropagation(); decidir.mutate('confirm'); }}
              >
                Confirmar resumo
              </button>
              <button
                type="button"
                className="btn bad ghost"
                disabled={decidir.isPending}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!nota.trim()) { avisar('diga o que está errado — vira lacuna para alguém resolver'); return; }
                  decidir.mutate('contest');
                }}
              >
                Contestar
              </button>
            </>
          )
          : (
            <span className="muted grow">
              {carimbo === 'nao_confirmada'
                ? 'Só o dono do módulo, um revisor ou admin confirma este resumo.'
                : carimbo ? `resumo ${rotulo(CARIMBO_DO_RESUMO, carimbo)}` : ''}
            </span>
          )}
        {minha && d.status === 'open' && (
          <button
            type="button"
            className="btn"
            onClick={(e) => { e.stopPropagation(); setFechando(true); }}
          >
            Fechar com resumo
          </button>
        )}
        {d.summary_unit_key && (
          <button
            type="button"
            className="btn ghost sm"
            onClick={(e) => { e.stopPropagation(); ir(d.summary_unit_key!); }}
          >
            abrir íntegra
          </button>
        )}
      </div>

      {fechando && (
        <FormularioDeFechamento
          slug={slug}
          id={id}
          aoFechar={() => setFechando(false)}
          aoPublicar={recarregar}
        />
      )}
    </>
  );
}

/**
 * Fechar pelo painel: o caminho normal é o agente chamar `session_close`, mas
 * quem trabalhou no terminal e esqueceu precisa de um lugar para escrever o
 * resumo (knowledge.js:410-435).
 */
function FormularioDeFechamento({ slug, id, aoFechar, aoPublicar }: {
  slug: string;
  id: string;
  aoFechar: () => void;
  aoPublicar: () => Promise<unknown>;
}) {
  const avisar = useRecado();
  const [corpo, setCorpo] = useState('');
  const [vinculos, setVinculos] = useState('');

  const enviar = useMutation({
    mutationFn: () => postar<{ version?: number }>(
      `/api/${slug}/sessions/${encodeURIComponent(id)}/close`,
      {
        summary_md: corpo.trim(),
        links: vinculos.split(',').map((s) => s.trim()).filter(Boolean),
      },
    ),
    onSuccess: async (r) => {
      avisar(`resumo publicado v${r.version} — já recuperável, aguardando confirmação`);
      aoFechar();
      await aoPublicar();
    },
    onError: (e) => avisar(e instanceof Error ? e.message : String(e)),
  });

  return (
    <div className="prop open pending" style={{ marginTop: 10 }}>
      <div className="prop-body">
        <div className="sbs" style={{ gridTemplateColumns: '1fr' }}>
          <div className="col">
            <div className="col-t">
              o que ficou de conhecimento (markdown) — vira unidade curada e entra na busca
            </div>
            <textarea
              aria-label="resumo da sessão"
              style={{ minHeight: 180 }}
              placeholder={'## O que era\n...\n\n## O que descobri\n...\n\n## O que fazer na próxima vez\n...'}
              value={corpo}
              onChange={(e) => setCorpo(e.target.value)}
            />
          </div>
        </div>
        <div className="prop-acts">
          <input
            value={vinculos}
            onChange={(e) => setVinculos(e.target.value)}
            placeholder="arquivos tocados, separados por vírgula (é o que liga este resumo ao código)"
          />
          <button
            type="button"
            className="btn"
            disabled={enviar.isPending}
            onClick={() => {
              if (!corpo.trim()) { avisar('sem resumo não há memória — escreva o que ficou'); return; }
              enviar.mutate();
            }}
          >
            Fechar sessão
          </button>
          <button type="button" className="btn ghost" onClick={aoFechar}>cancelar</button>
        </div>
      </div>
    </div>
  );
}
